/* Gift In One print surfaces: one canonical, bounded configuration contract.
 * A nonempty variant value replaces the whole product value. Invalid settings
 * are never silently converted into printable dimensions or inherited rules.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.GIOMultiSurfaceCore = factory();
}(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  var SCHEMA = 'gio-print-surfaces-v1', TEMPLATE_SCHEMA = 'gio-multi-surface-template-v1', MAX_SURFACES = 8, MAX_JSON = 16384;
  var SLUG = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;
  var own = Function.call.bind(Object.prototype.hasOwnProperty);
  function fail(message) { var error = new Error(message); error.code = 'GIO_MULTI_SURFACE_CONFIGURATION'; throw error; }
  function blank(value) { return value == null || typeof value === 'string' && !value.trim(); }
  function object(value) { return value !== null && typeof value === 'object' && !Array.isArray(value); }
  function keys(value, allowed, label) {
    if (!object(value)) fail(label + ' must be an object.');
    Object.keys(value).forEach(function (key) { if (allowed.indexOf(key) < 0) fail(label + ' has an unsupported field: ' + key + '.'); });
  }
  function slug(value, maximum, label) {
    if (typeof value !== 'string' || value.length > maximum || !SLUG.test(value)) fail(label + ' must be a lowercase ID using letters, numbers and single hyphens.');
    return value;
  }
  function dimension(value, label) {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 1 || value > 20000) fail(label + ' must be an integer from 1 to 20000 pixels.');
    return value;
  }
  function parse(value) {
    if (typeof value === 'string') {
      if (value.length > MAX_JSON) fail('Print-surface configuration is too large.');
      try { value = JSON.parse(value); } catch (_) { fail('Print-surface configuration is not valid JSON.'); }
    }
    return value;
  }
  function normalize(value) {
    value = parse(value);
    keys(value, ['schema', 'enabled', 'surfaces'], 'Print-surface configuration');
    if (value.schema !== SCHEMA) fail('Unsupported print-surface configuration schema.');
    var enabled = own(value, 'enabled') ? value.enabled : true;
    if (typeof enabled !== 'boolean') fail('enabled must be true or false.');
    if (!enabled) {
      if (own(value, 'surfaces') && (!Array.isArray(value.surfaces) || value.surfaces.length)) fail('Disabled print-surface configuration must have an empty surfaces list.');
      return { schema: SCHEMA, enabled: false, surfaces: [] };
    }
    if (!Array.isArray(value.surfaces) || !value.surfaces.length || value.surfaces.length > MAX_SURFACES) fail('Choose from 1 to 8 print surfaces.');
    var seen = Object.create(null);
    var surfaces = value.surfaces.map(function (item) {
      keys(item, ['id', 'label', 'template', 'width', 'height'], 'Print surface');
      var id = slug(item.id, 48, 'Surface ID');
      if (seen[id]) fail('Print-surface IDs must be unique.');
      seen[id] = true;
      if (typeof item.label !== 'string' || !item.label.trim() || Array.from(item.label).length > 80 || /[\u0000-\u001f\u007f]/.test(item.label)) fail('Each surface needs a name of 1 to 80 characters without control characters.');
      var width = dimension(item.width, 'HD width'), height = dimension(item.height, 'HD height');
      if (width * height > 400000000 || width / height < 0.25 || width / height > 4) fail('HD output must be at most 400 million pixels, with an aspect ratio between 1:4 and 4:1.');
      return { id: id, label: item.label.trim(), template: slug(item.template, 80, 'Template ID'), width: width, height: height };
    });
    var result = { schema: SCHEMA, enabled: true, surfaces: surfaces };
    if (JSON.stringify(result).length > MAX_JSON) fail('Print-surface configuration is too large.');
    return result;
  }
  function resolve(productValue, variantValue) {
    var source = !blank(variantValue) ? 'variant' : !blank(productValue) ? 'product' : 'none';
    if (source === 'none') return { configured: false, enabled: false, source: source, config: null, surfaces: [] };
    var config = normalize(source === 'variant' ? variantValue : productValue);
    return { configured: true, enabled: config.enabled, source: source, config: config, surfaces: config.surfaces };
  }
  function inspect(productValue, variantValue) {
    try { var result = resolve(productValue, variantValue); result.valid = true; return result; }
    catch (error) { return { configured: true, enabled: true, valid: false, source: !blank(variantValue) ? 'variant' : 'product', config: null, surfaces: [], errorCode: error.code || 'GIO_MULTI_SURFACE_CONFIGURATION', message: String(error.message || 'Invalid print-surface configuration.') }; }
  }
  function canonical(value) { return JSON.stringify(normalize(value)); }
  function surface(value, surfaceId) {
    slug(surfaceId, 48, 'Surface ID');
    var config = normalize(value), found = config.surfaces.find(function (item) { return item.id === surfaceId; });
    if (!found) fail('The requested print surface is not enabled in this configuration.');
    return found;
  }
  function references(value) {
    if (blank(value)) return [];
    var config = normalize(value), ids = [];
    config.surfaces.forEach(function (item) { if (ids.indexOf(item.template) < 0) ids.push(item.template); });
    return ids;
  }
  // Parent templates share the existing file-template namespace. Products store
  // only this ID in custom.gio_template; dimensions live in the parent file.
  function templateKey(id) {
    slug(id, 80, 'Template ID');
    if (id === 'index' || id === 'catalog-index') fail('This template ID is reserved.');
    return 'backimg/gio-templates/designs/' + id + '.json';
  }
  function normalizeTemplate(value, expectedId) {
    value = parse(value);
    keys(value, ['schema', 'id', 'title', 'description', 'surfaces'], 'Multi-surface template');
    if (value.schema !== TEMPLATE_SCHEMA) fail('Unsupported multi-surface template schema.');
    templateKey(value.id);
    if (expectedId != null && value.id !== expectedId) fail('Template ID does not match its storage key.');
    if (typeof value.title !== 'string' || !value.title.trim() || Array.from(value.title).length > 120 || /[\u0000-\u001f\u007f]/.test(value.title)) fail('Template title must contain 1 to 120 characters.');
    var description = own(value, 'description') ? value.description : '';
    if (typeof description !== 'string' || Array.from(description).length > 4000 || /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(description)) fail('Template description must contain at most 4000 characters.');
    var config = normalize({ schema: SCHEMA, enabled: true, surfaces: value.surfaces });
    if (config.surfaces.some(function (item) { return item.template === value.id; })) fail('A multi-surface template cannot reference itself.');
    var result = { schema: TEMPLATE_SCHEMA, id: value.id, title: value.title.trim(), description: description, surfaces: config.surfaces };
    if (JSON.stringify(result).length > MAX_JSON || utf8Bytes(JSON.stringify(result)) > MAX_JSON) fail('Multi-surface template is too large.');
    return result;
  }
  function templateConfig(value, expectedId) {
    return normalize({ schema: SCHEMA, enabled: true, surfaces: normalizeTemplate(value, expectedId).surfaces });
  }
  var PROPERTY_BYTES = 200, PROPERTY_CHUNKS = 128;
  function propertyName(name) {
    if (name !== '_MultiSurfaceConfig' && name !== '_MultiSurfaceJobs') fail('Unsupported print-surface property name.');
    return name;
  }
  // Match UTF-8 encoding, including U+FFFD substitution for an unpaired UTF-16
  // surrogate. Avoid depending on TextEncoder during the emergency cart path.
  function utf8Bytes(value) {
    var count = 0;
    for (var index = 0; index < value.length; index++) {
      var unit = value.charCodeAt(index), next = value.charCodeAt(index + 1);
      if (unit <= 0x7f) count++;
      else if (unit <= 0x7ff) count += 2;
      else if (unit >= 0xd800 && unit <= 0xdbff && next >= 0xdc00 && next <= 0xdfff) { count += 4; index++; }
      else count += 3;
    }
    return count;
  }
  function propertyChunks(name, value) {
    propertyName(name);
    if (typeof value !== 'string' || value.length > MAX_JSON || utf8Bytes(value) > MAX_JSON) fail('Print-surface property is not bounded text.');
    var result = {}, part = '', size = 0, index = 0;
    if (utf8Bytes(value) <= PROPERTY_BYTES) { result[name] = value; return result; }
    // for...of visits Unicode code points, so an astral character is never
    // divided between two Shopify properties. Unpaired units remain intact.
    Array.from(value).forEach(function (character) {
      var bytes = utf8Bytes(character);
      if (size + bytes > PROPERTY_BYTES) { result[name + index++] = part; part = ''; size = 0; }
      part += character; size += bytes;
    });
    if (part) result[name + index++] = part;
    if (index > PROPERTY_CHUNKS) fail('Too many print-surface property chunks.');
    return result;
  }
  function propertyValue(properties, name, maxBytes) {
    propertyName(name);
    if (maxBytes == null) maxBytes = MAX_JSON;
    if (!Number.isInteger(maxBytes) || maxBytes < 1 || maxBytes > MAX_JSON) fail('Invalid print-surface property byte limit.');
    if (properties == null) return undefined;
    if (!Array.isArray(properties) && !object(properties)) fail('Print-surface properties must be an array or object.');
    var items = Array.isArray(properties) ? properties : Object.keys(properties).map(function (key) { return { name: key, value: properties[key] }; });
    var direct, hasDirect = false, count = 0, total = 0, parts = Object.create(null);
    items.forEach(function (item) {
      if (!item || typeof item.name !== 'string' || item.name.indexOf(name) !== 0) return;
      if (typeof item.value !== 'string') fail('Print-surface property values must be text.');
      if (item.name === name) {
        if (hasDirect) fail('A print-surface property is duplicated.');
        hasDirect = true; direct = item.value;
        // Preserve previously saved direct values within the total limit. New
        // writers always split values longer than PROPERTY_BYTES.
        if (direct.length > maxBytes || utf8Bytes(direct) > maxBytes) fail('Print-surface property exceeds its total byte limit.');
        return;
      }
      var suffix = item.name.slice(name.length);
      if (!/^(0|[1-9][0-9]*)$/.test(suffix) || suffix.length > 3 || Number(suffix) >= PROPERTY_CHUNKS) fail('Invalid print-surface chunk index.');
      var index = Number(suffix);
      if (own(parts, index)) fail('A print-surface property chunk is duplicated.');
      if (!item.value.length || item.value.length > PROPERTY_BYTES) fail('Invalid print-surface property chunk size.');
      var bytes = utf8Bytes(item.value);
      if (bytes > PROPERTY_BYTES) fail('Invalid print-surface property chunk size.');
      total += bytes;
      if (total > maxBytes || ++count > PROPERTY_CHUNKS) fail('Print-surface chunks exceed their total limit.');
      parts[index] = item.value;
    });
    if (hasDirect && count) fail('Direct and chunked print-surface properties cannot be mixed.');
    if (hasDirect) return direct;
    if (!count) return undefined;
    var joined = '';
    for (var index = 0; index < count; index++) {
      if (!own(parts, index)) fail('A print-surface property chunk is missing.');
      var previous = joined.charCodeAt(joined.length - 1), next = parts[index].charCodeAt(0);
      if (previous >= 0xd800 && previous <= 0xdbff && next >= 0xdc00 && next <= 0xdfff) fail('A Unicode character was split between print-surface properties.');
      joined += parts[index];
    }
    return joined;
  }
  return { schema: SCHEMA, templateSchema: TEMPLATE_SCHEMA, templateKey: templateKey, normalizeTemplate: normalizeTemplate, templateConfig: templateConfig, maxSurfaces: MAX_SURFACES, maxJSON: MAX_JSON, normalize: normalize, resolve: resolve, inspect: inspect, canonical: canonical, surface: surface, references: references, isBlank: blank, propertyChunks: propertyChunks, propertyValue: propertyValue, maxPropertyBytes: PROPERTY_BYTES, maxPropertyChunks: PROPERTY_CHUNKS };
}));
